import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  faCircle,
  faEdit, faEllipsisVertical, faFloppyDisk, faFolderMinus, faFolderPlus, faPlus, faStar, faStarOfLife, faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dropdown, Input, message, notification, Popconfirm, Popover, Space, Spin, Tooltip,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { filterTypeEnums, SERVICE_ENDPOINT } from '../../../apis/constants';
import {
  addSavedFilterView,
  deleteSavedFilterView, getSavedFilterView, markOrRemoveDefaultSavedFilterView, updateSavedFilterView,
} from './helpers';
import './style.scss';

const UserSavedFilterViewSelector = ({
  selectedFilterView, onChangeTab, currentlyAppliedFilters, entityName, setSavedFilterViews, setSavedFilterViewsLoading, setIsUpdateFilterSate, callback, systemDefaultUserView,
  user,
}) => {
  const [tabName, setTabName] = useState('');
  const [tabNameError, setTabNameError] = useState(false);
  const [userTabOptions, setUserTabOptions] = useState([]);
  const [savedFilterViewData, setSavedFilterViewData] = useState([]);
  const [savedFilterViewDataLoading, setSavedFilterViewDataLoading] = useState(false);
  // const [addSavedFilterViewLoading, setAddSavedFilterViewLoading] = useState(false);
  // const [updateSavedFilterViewLoading, setUpdateSavedFilterViewLoading] = useState(false);
  // const [deleteSavedFilterViewLoading, setDeleteSavedFilterViewLoading] = useState(false);
  // const [markOrRemoveDefaultSavedFilterViewLoading, setMarkOrRemoveDefaultSavedFilterViewLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const fetchSavedFilterViews = async () => {
    setSavedFilterViewDataLoading(true);
    const { url } = SERVICE_ENDPOINT.SAVED_FILTER_VIEW({ tenantId: user?.tenant_info?.tenant_id });

    const savedFilterViews = await getSavedFilterView({
      payload: {
        query: {
          entity_name: entityName,
        },
        url,
      },
    });

    if (savedFilterViews.success && savedFilterViews.data) {
      const savedFilterData = savedFilterViews.data?.saved_filters || [];
      setSavedFilterViewData(savedFilterData);
      setSavedFilterViews(savedFilterData);
    } else {
      message.warning('Failed to fetch saved views');
    }
    setSavedFilterViewDataLoading(false);
    setSavedFilterViewsLoading(false);
    setLoading(false);
    if (callback) {
      callback();
    }
  };

  useEffect(() => {
    setSavedFilterViewsLoading(true);
    fetchSavedFilterViews();
  }, []);

  useEffect(() => {
    const tabOptions = [systemDefaultUserView, ...savedFilterViewData];

    tabOptions.forEach((item) => {
      item.value = item.filter_id;
      item.label = item.filter_name;
    });

    setUserTabOptions(tabOptions);
  }, [savedFilterViewData]);

  const TruncatedText = ({ text, maxLength, showTooltip }) => {
    let tempText = text;
    if (tempText.length > maxLength) {
      tempText = `${tempText.substring(0, maxLength)}...`;
    }

    if (showTooltip) {
      return (
        <Tooltip title={text}>
          {tempText}
        </Tooltip>
      );
    }

    return tempText;
  };

  function areArraysSameType(arr1, arr2) {
    const getUniformType = (arr) => {
      if (!Array.isArray(arr)) return null;
      if (arr.length === 0) return 'empty';

      const firstType = typeof arr[0];
      if (firstType !== 'string' && firstType !== 'number') return null;

      return arr.every((item) => typeof item === firstType) ? firstType : null;
    };

    const type1 = getUniformType(arr1);
    const type2 = getUniformType(arr2);

    if (type1 === 'empty' && type2 === 'empty') return true;

    if (type1 === 'empty') return type2 === 'string' || type2 === 'number';
    if (type2 === 'empty') return type1 === 'string' || type1 === 'number';

    return type1 && type2 && type1 === type2;
  }

  function deepEqual(a, b) {
    if (a === b) return true;

    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
      return false;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      let _a = a;
      let _b = b;
      if (areArraysSameType(a, b)) {
        _a = JSON.parse(JSON.stringify(a)).sort((x, y) => x - y);
        _b = JSON.parse(JSON.stringify(b)).sort((x, y) => x - y);
      }

      for (let i = 0; i < _a.length; i++) {
        if (!deepEqual(_a[i], _b[i])) return false;
      }
      return true;
    }

    if (Array.isArray(a) !== Array.isArray(b)) return false;

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
    }

    return true;
  }

  function compareFilterArrays(arr1, arr2) {
    const map1 = new Map();
    const map2 = new Map();

    const dateRangeTypeFiltersArr1 = arr1.filter((obj) => obj.filter_type === filterTypeEnums.DATE_RANGE);
    const dateRangeTypeFiltersArr2 = arr2.filter((obj) => obj.filter_type === filterTypeEnums.DATE_RANGE);

    const remainingFiltersArr1 = arr1.filter((obj) => obj.filter_type !== filterTypeEnums.DATE_RANGE);
    const remainingFiltersArr2 = arr2.filter((obj) => obj.filter_type !== filterTypeEnums.DATE_RANGE);

    for (const obj of remainingFiltersArr1) {
      map1.set(obj.filter_key, obj.filter_value);
    }

    for (const obj of remainingFiltersArr2) {
      map2.set(obj.filter_key, obj.filter_value);
    }

    const allKeys = new Set([...map1.keys(), ...map2.keys()]);
    const differences = [];

    for (const key of allKeys) {
      const val1 = map1.get(key);
      const val2 = map2.get(key);

      if (!deepEqual(val1, val2)) {
        differences.push({
          key,
          value_in_arr1: val1,
          value_in_arr2: val2,
        });
      }
    }

    for (const obj of dateRangeTypeFiltersArr1) {
      map1.set(obj.filter_key, obj);
    }

    for (const obj of dateRangeTypeFiltersArr2) {
      map2.set(obj.filter_key, obj);
    }
    const allDateRangeKeys = new Set([...map1.keys(), ...map2.keys()]);

    for (const key of allDateRangeKeys) {
      const val1 = map1.get(key);
      const val2 = map2.get(key);

      if (!deepEqual(val1?.date_range_type, val2?.date_range_type)) {
        if (!deepEqual(val1?.filter_value, val2?.filter_value)) {
          differences.push({
            key,
            value_in_arr1: val1,
            value_in_arr2: val2,
          });
        }
      }
    }
    return differences;
  }

  const isDifferenceInFilter = compareFilterArrays(selectedFilterView?.filters || [], currentlyAppliedFilters || []).length > 0;

  const handleSaveTabName = (item) => {
    if (!tabName.trim()) {
      setTabNameError(true);
      return;
    }
    setTabNameError(false);
    setIsUpdateFilterSate(false);
    setLoading(true);
    updateSavedFilterView({
      payload: {
        body: {
          filter_name: tabName.trim(),
          entity_name: entityName,
          filters: item.filters,
        },
        url: SERVICE_ENDPOINT.SAVED_FILTER_VIEW_BY_ID({ tenantId: user?.tenant_info?.tenant_id, filterId: item.filter_id }).url,
      },
      callback: (responseData) => {
        if (responseData) fetchSavedFilterViews();
        setLoading(false);
      },
    });
  };

  const handleCancelEditTabName = () => {
    setTabName('');
    setTabNameError(false);
  };

  const handleDeleteTab = (item) => {
    setIsUpdateFilterSate(false);
    setLoading(true);
    deleteSavedFilterView({
      payload: {
        url: SERVICE_ENDPOINT.SAVED_FILTER_VIEW_BY_ID({ tenantId: user?.tenant_info?.tenant_id, filterId: item.filter_id }).url,
      },
      callback: (responseData) => {
        if (responseData) {
          fetchSavedFilterViews();
          if (item.filter_id === selectedFilterView.value) {
            const defaultView = userTabOptions.find((i) => i.is_default) || userTabOptions.find((i) => i.is_system_default);
            onChangeTab(defaultView);
          }
        }
        setLoading(false);
      },
    });
  };

  const handleMarkRemoveDefault = (item) => {
    setIsUpdateFilterSate(false);
    setLoading(true);
    if (item.is_default) {
      markOrRemoveDefaultSavedFilterView({
        payload: {
          url: SERVICE_ENDPOINT.SAVED_FILTER_VIEW_CLEAR_DEFAULT({ tenantId: user?.tenant_info?.tenant_id, filterId: item.filter_id }).url,
        },
        callback: (responseData) => {
          if (responseData) fetchSavedFilterViews();
          setLoading(false);
        },
      });
    } else {
      markOrRemoveDefaultSavedFilterView({
        payload: {
          url: SERVICE_ENDPOINT.SAVED_FILTER_VIEW_SET_DEFAULT({ tenantId: user?.tenant_info?.tenant_id, filterId: item.filter_id }).url,
        },
        callback: (responseData) => {
          if (responseData) fetchSavedFilterViews();
          setLoading(false);
        },
      });
    }
  };

  const handleUpdateFilter = (item) => {
    setIsUpdateFilterSate(false);
    setLoading(true);
    updateSavedFilterView({
      payload: {
        body: {
          filter_name: item.filter_name,
          entity_name: entityName,
          filters: currentlyAppliedFilters,
        },
        url: SERVICE_ENDPOINT.SAVED_FILTER_VIEW_BY_ID({ tenantId: user?.tenant_info?.tenant_id, filterId: item.filter_id }).url,
      },
      callback: (responseData) => {
        if (responseData) fetchSavedFilterViews();
        setLoading(false);
      },
    });
  };

  const handleSaveAsNewView = (item) => {
    if (!tabName.trim()) {
      setTabNameError(true);
      return;
    }

    // if (savedFilterViewData.length >= 5) {
    //   notification.error({
    //     message: 'You can only save upto 5 views.',
    //     placement: 'top',
    //     duration: 4,
    //   });
    //   return;
    // }

    setTabNameError(false);
    setIsUpdateFilterSate(false);
    setLoading(true);

    addSavedFilterView({
      payload: {
        body: {
          filter_name: tabName.trim(),
          entity_name: entityName,
          filters: currentlyAppliedFilters,
        },
        url: SERVICE_ENDPOINT.SAVED_FILTER_VIEW({ tenantId: user?.tenant_info?.tenant_id }).url,
      },
      callback: (responseData) => {
        if (responseData) {
          fetchSavedFilterViews();
          const selectedView = responseData[0];
          selectedView.value = selectedView.filter_id;
          selectedView.label = selectedView.filter_name;
          onChangeTab(selectedView);
        }
        setLoading(false);
      },
    });
  };

  const content = (item) => (
    <div>
      {
        (isDifferenceInFilter && selectedFilterView.filter_id === item.filter_id) && (
          <React.Fragment>
            {!item.is_system_default && (
              <div
                style={{ padding: '5px', cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Popconfirm
                  icon={<FontAwesomeIcon icon={faEdit} />}
                  placement="right"
                  trigger="click"
                  title="Update Current View"
                  // overlayClassName="edit-view-name-pop-confirm"
                  description="Are you sure to update this view filters?"
                  onConfirm={() => handleUpdateFilter(item)}
                  // onCancel={handleCancelEditTabName}
                  okText="Save"
                  cancelText="Cancel"
                >
                  <div>
                    <FontAwesomeIcon icon={faFloppyDisk} />
                    {' '}
                    Update Current View
                  </div>
                </Popconfirm>
              </div>
            )}
            <div
              style={{ padding: '5px', cursor: 'pointer' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Popconfirm
                icon={<FontAwesomeIcon icon={faEdit} />}
                // disabled={savedFilterViewData.length >= 5}
                placement="right"
                trigger="click"
                title="Add View Name"
                overlayClassName="edit-view-name-pop-confirm"
                description={(
                  <div style={{ width: 250 }}>
                    <Input
                      placeholder="Enter View Name"
                      value={tabName}
                      onChange={(e) => {
                        setTabName(e.target.value);
                        if (e.target.value.trim()) setTabNameError(false);
                      }}
                      style={{ width: '100%', marginBottom: tabNameError ? 0 : 8, borderColor: tabNameError ? 'red' : undefined }}
                    />
                    {tabNameError && (
                      <div style={{ color: 'red', marginBottom: 8, fontSize: '12px' }}>
                        View name is required.
                      </div>
                    )}
                  </div>
                )}
                onConfirm={() => handleSaveAsNewView(item)}
                onCancel={handleCancelEditTabName}
                okText="Save"
                okButtonProps={{ disabled: !tabName.trim(), loading: false }}
                cancelText="Cancel"
              >
                <div
                  onClick={() => {
                    setTabName('');
                  }}
                // style={{ color: savedFilterViewData.length >= 5 ? '#71717a' : '' }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {' '}
                  Save As New View
                </div>
              </Popconfirm>
            </div>
          </React.Fragment>
        )
      }
      {!item.is_system_default && (
        <React.Fragment>
          <div
            style={{ padding: '5px', cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Popconfirm
              icon={<FontAwesomeIcon icon={faEdit} />}
              placement="right"
              trigger="click"
              title="Change View Name"
              overlayClassName="edit-view-name-pop-confirm"
              description={(
                <div style={{ width: 250 }}>
                  <Input
                    placeholder="Enter View Name"
                    value={tabName}
                    onChange={(e) => setTabName(e.target.value)}
                    style={{ width: '100%', marginBottom: 8 }}
                  />
                </div>
              )}
              onConfirm={() => handleSaveTabName(item)}
              onCancel={handleCancelEditTabName}
              okText="Save"
              okButtonProps={{ disabled: !tabName.trim(), loading: false }}
              cancelText="Cancel"
            >
              <div onClick={() => {
                setTabName(item.label);
                setTabNameError(false);
              }}
              >
                <FontAwesomeIcon icon={faEdit} color="gray" />
                {' '}
                Edit
              </div>
            </Popconfirm>
          </div>
          <div
            style={{ padding: '5px', cursor: 'pointer', color: '#dc2626' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Popconfirm
              placement="right"
              title="Delete the Filter View"
              description="Are you sure to delete this view?"
              onConfirm={() => handleDeleteTab(item)}
              // onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <div>
                <FontAwesomeIcon icon={faTrash} />
                {' '}
                Delete
              </div>
            </Popconfirm>
          </div>
          <div
            style={{ padding: '5px', cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Popconfirm
              placement="right"
              title={item.is_default ? 'Remove Default' : 'Mark Default'}
              description={item.is_default ? 'Are you sure to remove this view from default?' : 'Are you sure to mark this view as default?'}
              onConfirm={() => handleMarkRemoveDefault(item)}
              // onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <div>
                {item.is_default ? <FontAwesomeIcon icon={faFolderMinus} color="gray" /> : <FontAwesomeIcon icon={faFolderPlus} color="gray" />}
                {' '}
                {item.is_default ? 'Remove From Default' : 'Mark As Default'}
              </div>
            </Popconfirm>
          </div>
        </React.Fragment>
      )}
    </div>
  );
  return (
    <React.Fragment>
      <div className="filter-view-dropdown-container">
        <Dropdown
          className="filter-view-dropdown"
          menu={{
            items: userTabOptions.map((item) => ({
              label: (
                <div className="filter-view-option">
                  <div className="flex-align-center-justify-between width-100">
                    <div className={`filter-view-name ${item.value === selectedFilterView.filter_id ? 'filter-view-name--active' : ''}`}>
                      <TruncatedText
                        text={item.label}
                        maxLength={25}
                        showTooltip
                      />
                      {' '}
                    </div>
                    {item.is_default && (
                      <div style={{
                        padding: '0px 1.5px',
                        cursor: 'pointer',
                      }}
                      >
                        <Tooltip title="Default"><FontAwesomeIcon icon={faStarOfLife} size="2xs" color="#0d9488" /></Tooltip>
                      </div>
                    )}
                  </div>

                  {!loading && item.value !== selectedFilterView.value
                    && !item.is_system_default
                    && (
                      <Popover
                        trigger={['click']}
                        placement="right"
                        content={content(item)}
                        arrow
                      >
                        <span
                          className="filter-view-options-toggle"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </span>
                      </Popover>
                    )}
                </div>
              ),
              key: item.value,
              disabled: item.value === selectedFilterView.filter_id,
            })),
            onClick: (e) => {
              const currentSelectedView = userTabOptions.find((item) => String(item.value) === String(e.key));
              onChangeTab(currentSelectedView);
            },
          }}
          trigger={['click']}
          placement="bottom"
        >
          <Space className="filter-view-trigger">
            <div
              className="filter-view-selected"
            >
              <TruncatedText
                text={selectedFilterView.filter_name || 'System Default View'}
                maxLength={25}
                showTooltip
              />
              {isDifferenceInFilter && <span><FontAwesomeIcon icon={faCircle} size="2xs" color="#fdba74" /></span>}
            </div>
            <DownOutlined />
          </Space>
        </Dropdown>

        {(savedFilterViewDataLoading || loading) ? (
          <Spin
            size="small"
            className="filter-view-loading-spinner"
          />
        ) : (selectedFilterView.is_system_default ? isDifferenceInFilter : true) && (
          <Popover
            trigger={['click']}
            placement="bottom"
            content={content(selectedFilterView)}
            arrow
          >
            <span
              className="filter-view-trigger-actions"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </span>
          </Popover>
        )}
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  UserReducers,
}) => ({
  user: UserReducers.user,
});

const mapDispatchToProps = (dispatch) => ({
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(UserSavedFilterViewSelector));
